import {
  PLAYERS_INDEX_PATH,
  toClubIndexPath,
  toPlayerProfilePath
} from './slugs';
import type { Player } from '@api-service/players';
import { FFH_HOSTNAME } from '@lib/constants';
import {
  buildBreadcrumbJsonLd,
  type BreadcrumbEntry
} from '@lib/page-utils/structured-data';

const POSITION_NOUN: Record<string, string> = {
  GK: 'goalkeeper',
  DEF: 'defender',
  MID: 'midfielder',
  FWD: 'forward'
};

export const describePosition = (position: string): string =>
  POSITION_NOUN[position] ?? 'player';

const formatPriceForCopy = (price: number | null): string | null =>
  price === null ? null : `£${price.toFixed(1)}m`;

export const buildPlayerTitle = (player: Player): string =>
  `${player.displayName} FPL stats, price and fixtures`;

const startsWithVowelSound = (value: string): boolean =>
  /^[aeiou]/i.test(value);

/**
 * Written from the same prediction-free fields the static render shows, so
 * the description cannot promise members-only numbers a crawler never sees.
 * Season totals are quoted only when the season is confirmed to be underway:
 * pre-season the row's totals are ambiguous (stale last-season numbers until
 * rows re-mint, zeroes afterwards), so the description leads on identity
 * instead. Passing `isSeasonUnderway: false` is therefore also the right
 * call when the league context cannot be fetched.
 */
export const buildPlayerDescription = (
  player: Player,
  { isSeasonUnderway }: { isSeasonUnderway: boolean }
): string => {
  const teamName = player.team.fullName ?? player.team.shortName;
  const article = startsWithVowelSound(teamName) ? 'an' : 'a';
  const identity = [
    `${player.displayName} is ${article} ${teamName} ${describePosition(
      player.position
    )} in Fantasy Premier League`,
    formatPriceForCopy(player.price) === null
      ? null
      : `priced at ${formatPriceForCopy(player.price)}`
  ]
    .filter(Boolean)
    .join(', ');

  const points = player.seasonStats?.points?.total ?? 0;
  const goals = player.seasonStats?.goals ?? 0;
  const assists = player.seasonStats?.assists ?? 0;
  const seasonSentence =
    isSeasonUnderway && points > 0
      ? `${points} points, ${goals} goals and ${assists} assists this season.`
      : null;

  return [
    `${identity}.`,
    seasonSentence,
    `See ${player.displayName}'s form, expected goals, fixtures and price history on Fantasy Football Hub.`
  ]
    .filter(Boolean)
    .join(' ');
};

export const buildPlayerBreadcrumbs = (
  teamName: string,
  clubSlug: string,
  displayName: string
): BreadcrumbEntry[] => [
  { label: 'Toolbox', path: '/tools' },
  { label: 'Player profiles', path: PLAYERS_INDEX_PATH },
  { label: teamName, path: toClubIndexPath(clubSlug) },
  { label: displayName }
];

/**
 * Mirrors the visible breadcrumb exactly - search engines cross-check the two
 * and ignore structured data that disagrees with the rendered page.
 */
// Re-exported so the player pages keep importing their SEO helpers from here.
export { buildBreadcrumbJsonLd, type BreadcrumbEntry };

export const buildPlayerPersonJsonLd = (
  player: Player,
  clubSlug: string,
  playerSlug: string
): Record<string, unknown> => {
  const teamName = player.team.fullName ?? player.team.shortName;

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: player.fullName ?? player.displayName,
    ...(player.fullName && player.fullName !== player.displayName
      ? { alternateName: player.displayName }
      : {}),
    url: `${FFH_HOSTNAME}${toPlayerProfilePath(clubSlug, playerSlug)}`,
    ...(player.imageUrl ? { image: player.imageUrl } : {}),
    jobTitle: 'Professional footballer',
    memberOf: {
      '@type': 'SportsTeam',
      name: teamName,
      sport: 'Association football'
    }
  };
};
